/**
 * User-configurable joystick binding, persisted to localStorage. Maps physical
 * gamepad axes/buttons to sim controls, with per-axis inversion and a deadzone.
 *
 * Each binding records WHICH physical device it came from (`pad`, the index
 * into navigator.getGamepads()), not just an axis/button number — with
 * multiple USB controllers connected, "axis 0" is ambiguous unless you also
 * know which device it's axis 0 *on*. This is what lets aileron come from
 * one stick and rudder pedals from a separate USB device, for example.
 */
export interface AxisBinding {
  pad: number;
  axis: number;
  invert: boolean;
}

export interface ButtonBinding {
  pad: number;
  button: number;
}

/** Structural shape shared by the native Gamepad API's Gamepad objects and
 * our WebHID PseudoGamepad adapter (src/input/WebHidInput.ts), so binding
 * lookups work identically regardless of which input source is active. */
export interface MinimalPad {
  id: string;
  axes: readonly number[];
  buttons: readonly { pressed: boolean }[];
}

export interface GamepadConfig {
  deadzone: number;
  aileron: AxisBinding;
  elevator: AxisBinding;
  rudder: AxisBinding;
  throttle: AxisBinding | null; // null => throttle via keyboard only
  // Independent per-wheel analog brake axes (e.g. HID toe-brake pedals) — on
  // top of, not instead of, the existing digital "brakes" button/keyboard
  // toggle. Unbound (null) by default since not everyone has toe brakes.
  brakeLeft: AxisBinding | null;
  brakeRight: AxisBinding | null;
  buttons: {
    flapsDown: ButtonBinding | null;
    flapsUp: ButtonBinding | null;
    trimDown: ButtonBinding | null;
    trimUp: ButtonBinding | null;
    brakes: ButtonBinding | null;
    gear: ButtonBinding | null;
  };
}

export function defaultConfig(): GamepadConfig {
  return {
    deadzone: 0.06,
    aileron: { pad: 0, axis: 0, invert: false },
    elevator: { pad: 0, axis: 1, invert: true }, // stick forward (−) = nose down
    rudder: { pad: 0, axis: 2, invert: false },
    throttle: { pad: 0, axis: 3, invert: false },
    brakeLeft: null,
    brakeRight: null,
    buttons: {
      flapsDown: { pad: 0, button: 0 },
      flapsUp: { pad: 0, button: 1 },
      trimDown: { pad: 0, button: 4 },
      trimUp: { pad: 0, button: 5 },
      brakes: { pad: 0, button: 2 },
      gear: { pad: 0, button: 3 },
    },
  };
}

const STORAGE_KEY = 'ifrtrainer.gamepadConfig.v2';
const OLD_STORAGE_KEY = 'ifrtrainer.gamepadConfig.v1'; // pre-multi-device format (no `pad`)

export function loadConfig(): GamepadConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // v1 configs have no `pad` field and are structurally incompatible
      // (buttons were plain numbers, not {pad,button}) — rather than trying
      // to salvage them, just fall back to defaults once.
      return defaultConfig();
    }
    const parsed = JSON.parse(raw);
    const d = defaultConfig();
    return {
      ...d,
      ...parsed,
      buttons: { ...d.buttons, ...(parsed.buttons ?? {}) },
    };
  } catch {
    return defaultConfig();
  } finally {
    try { localStorage.removeItem(OLD_STORAGE_KEY); } catch { /* ignore */ }
  }
}

export function saveConfig(cfg: GamepadConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* storage may be unavailable (private mode); config stays in-memory */
  }
}

/** Read a bound axis (from its own specific device) applying deadzone + inversion. */
export function readAxis(
  binding: AxisBinding | null,
  deadzone: number,
  pads: readonly (MinimalPad | null)[],
): number {
  if (!binding) return 0;
  const pad = pads[binding.pad];
  if (!pad || binding.axis >= pad.axes.length) return 0;
  let v = pad.axes[binding.axis];
  if (Math.abs(v) < deadzone) return 0;
  v = Math.sign(v) * ((Math.abs(v) - deadzone) / (1 - deadzone));
  return binding.invert ? -v : v;
}

/** Read a bound analog brake-pedal axis as a 0..1 position (rest = 0, fully
 * pressed = 1) — unlike readAxis, there's no deadzone/centring since a pedal
 * isn't a self-centring stick axis. Default (uninverted) assumes the common
 * pedal convention of -1 at rest / +1 fully pressed; the invert checkbox
 * flips that for hardware that reports the opposite. */
export function readPedalAxis(
  binding: AxisBinding | null,
  pads: readonly (MinimalPad | null)[],
): number {
  if (!binding) return 0;
  const pad = pads[binding.pad];
  if (!pad || binding.axis >= pad.axes.length) return 0;
  const raw = pad.axes[binding.axis];
  const v = binding.invert ? (1 - raw) / 2 : (1 + raw) / 2;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Read a bound button (from its own specific device). */
export function readButton(
  binding: ButtonBinding | null,
  pads: readonly (MinimalPad | null)[],
): boolean {
  if (!binding) return false;
  const pad = pads[binding.pad];
  return pad?.buttons[binding.button]?.pressed ?? false;
}
